// CLE-435 — task estimate parsing/formatting with hours + minutes support.
//
// Estimates are stored in the database as decimal HOURS (Decimal(6,2)), so a
// "1h 30m" estimate is persisted as 1.5. These helpers let the UI accept and
// display human duration strings while keeping the stored unit unchanged.
//
// Accepted input formats (case-insensitive, whitespace-tolerant):
//   "2h"        -> 2
//   "1h30m"     -> 1.5
//   "h30m"      -> 1.5   (bare "h" means 1 hour, e.g. "H30M")
//   "h"         -> 1     (bare "h" alone means 1 hour)
//   "30m"       -> 0.5
//   "90m"       -> 1.5   (minutes >= 60 are allowed and roll over)
//   "1h 30m"    -> 1.5
//   "2"         -> 2     (plain number = hours, backwards compatible)
//   "2.5"       -> 2.5
//   ""          -> null  (cleared)

export interface ParseEstimateResult {
  /** Estimate in decimal hours, or null when the input clears the estimate. */
  hours: number | null;
  /** True when the input could not be understood. */
  error: boolean;
}

/**
 * Parse a user-entered estimate string into decimal hours.
 *
 * Returns { hours: null } for empty input (clears the estimate) and
 * { error: true } for anything that cannot be interpreted.
 */
export function parseEstimate(input: string): ParseEstimateResult {
  const trimmed = input.trim();
  if (trimmed === '') return { hours: null, error: false };

  const normalized = trimmed.toLowerCase().replace(/\s+/g, '');

  // Plain number (with optional comma decimal) = hours. Keeps the old
  // number-only behaviour working.
  if (/^\d+([.,]\d+)?$/.test(normalized)) {
    const hours = Number(normalized.replace(',', '.'));
    if (Number.isNaN(hours) || hours < 0) return { hours: null, error: true };
    return { hours: roundHours(hours), error: false };
  }

  // Duration form with h/m suffixes: optional hours, optional minutes, in that
  // order. At least one component must be present. The hours number is itself
  // optional so a bare "h" ("H30M", "H") is read as 1 hour, matching how users
  // write it. Capture group 1 marks the presence of the hours segment; group 2
  // is its number (undefined when written bare).
  const match = normalized.match(/^((\d+(?:[.,]\d+)?)?h)?(?:(\d+(?:[.,]\d+)?)m)?$/);
  if (!match || (match[1] === undefined && match[3] === undefined)) {
    return { hours: null, error: true };
  }

  // Hours segment present but no number ("h") => 1 hour; with a number => that
  // number; absent entirely => 0.
  const hoursPart =
    match[1] !== undefined ? (match[2] !== undefined ? Number(match[2].replace(',', '.')) : 1) : 0;
  const minsPart = match[3] !== undefined ? Number(match[3].replace(',', '.')) : 0;
  if (Number.isNaN(hoursPart) || Number.isNaN(minsPart)) {
    return { hours: null, error: true };
  }

  const hours = hoursPart + minsPart / 60;
  if (hours < 0) return { hours: null, error: true };
  return { hours: roundHours(hours), error: false };
}

// Store at 2-decimal precision to match the DB Decimal(6,2) column and avoid
// float noise (e.g. 1/3 hour = 20m -> 0.33).
function roundHours(hours: number): number {
  return Math.round(hours * 100) / 100;
}

/**
 * Format decimal hours as a human duration string like "1h 30m".
 *
 * - null -> the provided empty placeholder (default "—")
 * - whole hours -> "2h"
 * - whole hours + minutes -> "1h 30m"
 * - under an hour -> "30m"
 */
export function formatEstimate(hours: number | null, empty = '—'): string {
  if (hours == null) return empty;
  if (hours === 0) return '0m';

  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}
